import { EssentialCardItem, EssentialsCarouselProps } from '@msc-b2c/interfaces/types';
import EssentialsCarouselCard from './EssentialsCarouselCard';
import { Carousel } from '@msc-b2c/shared-components';
import styles from './essentialscarousel.module.scss';
import { breakpoints, useWindowSize } from '@msc-b2c/shared-utils';
import { useEffect, useState } from 'react';

const EssentialsCarousel = ({ aemConfigurationProps }: EssentialsCarouselProps) => {
  const { sliderOptions, contentOptions } = aemConfigurationProps;
  const { width } = useWindowSize();

  const {
    showSliderMobile,
    showSliderTablet,
    showSliderDesktop,
    showArrowsMobile,
    showArrowsTablet,
    showArrowsDesktop,
    leftArrowIcon,
    rightArrowIcon,
  } = sliderOptions;
  const { essentialCardItems } = contentOptions;

  const [isMobile, setIsMobile] = useState(width < breakpoints.md);
  const [isTablet, setIsTablet] = useState(width >= breakpoints.md && width < breakpoints.lg);
  const [isDesktop, setIsDesktop] = useState(width >= breakpoints.lg);

  useEffect(() => {
    const updateViewport = () => {
      setIsMobile(width < breakpoints.md);
      setIsTablet(width >= breakpoints.md && width < breakpoints.lg);
      setIsDesktop(width >= breakpoints.lg);
    };

    updateViewport();
  }, [width]);

  const showArrows =
    !!(leftArrowIcon && rightArrowIcon) &&
    ((isMobile && showArrowsMobile) ||
      (isTablet && showArrowsTablet) ||
      (isDesktop && showArrowsDesktop));

  const extractNavigationLink = (cardLink: string) => {
    // Function to normalize paths: remove `.html` and leading slashes
    const cleanPath = (path: string) => path.replaceAll(/\.html/g, '').replace(/^\/+/, '');

    const currentPath = cleanPath(globalThis.location.pathname);
    const normalizedCardLink = cleanPath(cardLink);

    const currentSegments = currentPath.split('/');
    const cardSegments = normalizedCardLink.split('/');

    // Find the first differing index
    let commonIndex = 0;
    while (
      commonIndex < currentSegments.length &&
      commonIndex < cardSegments.length &&
      currentSegments[commonIndex] === cardSegments[commonIndex]
    ) {
      commonIndex++;
    }

    return cardSegments.slice(commonIndex).join('/');
  };

  if (essentialCardItems.length > 0) {
    const skeleton = document.querySelector('.essentials-carousel-skeleton');
    if (skeleton) {
      skeleton.remove(); // Removes the element from the DOM
    }
  }

  return (
    <div className={styles.essentialsCarousel}>
      <Carousel
        id={'carousel-essentials'}
        data={essentialCardItems.length > 0 ? essentialCardItems : []}
        swiperOptions={{
          slidesPerView: 'auto',
          spaceBetween: 16,
          loop: false,
          speed: 200,
          breakpointsBase: 'container',
        }}
        isProgressBar={true}
        isFreeMode={true}
        showIndicator={{
          mobile: false,
          desktop: false,
          tablet: false,
        }}
        showInNavIndicator={{
          mobile: showSliderMobile,
          tablet: showSliderTablet,
          desktop: showSliderDesktop,
        }}
        showArrows={showArrows}
        leftArrowIcon={leftArrowIcon}
        rightArrowIcon={rightArrowIcon}
        styleOption={'-circular-actions -medium-actions -light'}
        renderItem={(item, index) => {
          const cardItem = item as EssentialCardItem;

          // Group every 2nd and 3rd card together
          if (index % 3 === 1) {
            const nextCard = essentialCardItems[index + 1];
            if (nextCard) {
              return (
                <div
                  key={`essentials-carousel-group-${index}`}
                  className={styles.carouselGroup}
                >
                  <EssentialsCarouselCard
                    cardLabel={cardItem.cardLabel}
                    cardLink={{
                      url: cardItem.cardLink.url
                        ? extractNavigationLink(cardItem.cardLink.url)
                        : '',
                      valid: cardItem.cardLink.valid,
                    }}
                    imageUrl={cardItem.imageUrl}
                    imageAlt={cardItem.imageAlt}
                    cardIndex={index}
                  />
                  <EssentialsCarouselCard
                    key={`essentials-carousel-card-${index + 1}`}
                    cardLabel={nextCard.cardLabel}
                    cardLink={{
                      url: nextCard.cardLink.url
                        ? extractNavigationLink(nextCard.cardLink.url)
                        : '',
                      valid: nextCard.cardLink.valid,
                    }}
                    imageUrl={nextCard.imageUrl}
                    imageAlt={nextCard.imageAlt}
                    cardIndex={index + 1}
                  />
                </div>
              );
            } else {
              return (
                <EssentialsCarouselCard
                  key={`essentials-carousel-card-${index}`}
                  cardLabel={cardItem.cardLabel}
                  cardLink={{
                    url: cardItem.cardLink.url ? extractNavigationLink(cardItem.cardLink.url) : '',
                    valid: cardItem.cardLink.valid,
                  }}
                  imageUrl={cardItem.imageUrl}
                  imageAlt={cardItem.imageAlt}
                  cardIndex={index}
                />
              );
            }
          }

          // Render single cards for the rest
          if (index % 3 === 0) {
            return (
              <EssentialsCarouselCard
                key={`essentials-carousel-card-${index}`}
                cardLabel={cardItem.cardLabel}
                cardLink={{
                  url: cardItem.cardLink.url ? extractNavigationLink(cardItem.cardLink.url) : '',
                  valid: cardItem.cardLink.valid,
                }}
                imageUrl={cardItem.imageUrl}
                imageAlt={cardItem.imageAlt}
                cardIndex={index}
              />
            );
          }

          return <div className={styles.empty}></div>;
        }}
      />
    </div>
  );
};

export default EssentialsCarousel;
